import { Skeleton } from './Skeleton';
import { cn } from '../../utils/cn';

/**
 * Skeleton SHAPE-MATCHED a la tabla del design system (`ui/Table`):
 * cabecera tenue + N filas de celdas. Theme-aware (compuesto desde el
 * primitivo `ui/Skeleton` → `bg-bg-subtle`, respeta dark mode y AA).
 *
 * Props:
 *  - rows: cantidad de filas (default 8)
 *  - columns: número de columnas, o array de "hints" de ancho por columna
 *    (cualquier clase Tailwind de ancho, p.ej. ['w-24','w-40','w-16']).
 *    Si es número, se reparte un set de anchos por defecto.
 *  - className: clases extra para el contenedor.
 */
export function TableSkeleton({ rows = 8, columns = 6, className }) {
  // Normaliza `columns` a un array de hints de ancho.
  const widthHints = Array.isArray(columns)
    ? columns
    : Array.from({ length: Math.max(1, columns) }, (_, i) =>
        // Patrón de anchos variados para que no se vea como bloques uniformes.
        ['w-24', 'w-32', 'w-20', 'w-40', 'w-16', 'w-28'][i % 6],
      );
  const colCount = widthHints.length;

  return (
    <div
      className={cn('w-full overflow-hidden rounded-lg border border-border', className)}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {/* Cabecera */}
      <div className="flex items-center gap-4 bg-bg-muted px-4 py-3">
        {widthHints.map((w, i) => (
          <div key={i} className="flex-1">
            <Skeleton className={cn('h-3', w)} />
          </div>
        ))}
      </div>
      {/* Filas */}
      <div className="divide-y divide-border bg-bg">
        {Array.from({ length: Math.max(1, rows) }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-4 py-3">
            {Array.from({ length: colCount }).map((_, c) => (
              <div key={c} className="flex-1">
                <Skeleton
                  className={cn(
                    'h-4',
                    // Anchos ligeramente distintos por fila para un look orgánico.
                    ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-2/5'][(r + c) % 5],
                  )}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TableSkeleton;
